import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, SectionCard, FormSection, Input, RadioGroup, Switch, ImageUploadField,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogClose,
  toast,
} from '@/components/ui';
import { useImageUploadQueue } from '@/lib/upload';
import { fetchAllEvents, useEventQuery, useEventMutation } from '../hooks/useEventsQuery';
import { deriveEventStatus, nextEventSortOrder, todayKst } from '../lib/eventListFilter';
import { useEventImageUpload } from '../hooks/useEventImageUpload';
import { instantToLocalInput, isEndBeforeStart, localInputToInstant } from '../lib/datetimeLocal';
import { EventTargetForm } from '../components/EventTargetForm';
import { EventCollectionFieldsForm } from '../components/EventCollectionFieldsForm';
import { EventConsentTermsForm } from '../components/EventConsentTermsForm';
import { PreviewUrlButton } from '../preview/PreviewUrlButton';
import { LocaleHtmlTabs } from '../components/LocaleHtmlTabs';
import { type ContentLocale } from '../lib/contentLocales';
import { buildLocaleMultiFieldTranslations, pickLocaleHtml } from '../lib/localeContent';
import type { EventRequest, EventTranslation } from '../types/cms';
import { editorImageGuideFor } from '@/lib/upload/uploadGuide';

// QA-122 — 이벤트 캠페인 per-locale 필드 키(EventTranslation = {title, description}).
const EVENT_TITLE_FIELD = 'title';
const EVENT_DESC_FIELD = 'description';

const EMPTY: EventRequest = {
  eventId: '', eventKey: '', title: '', description: '',
  startDate: '', endDate: '', thumbnailUrl: '',
  status: 'UPCOMING', isActive: true, sortOrder: 0,
  winnerCount: null, drawingMethod: null, contactChannel: null, consolationMessage: '',
  // QA-127: 신규 이벤트는 기본 발표 이벤트(true) — 기존 동작과 동일(당첨자 행 노출).
  winnerAnnounceEnabled: true,
  displayReservationEnabled: false,
  // 15-D-3(ID86): 시작/종료 시각. datetime-local 표시값('YYYY-MM-DDTHH:mm', 로컬). 빈='' = 시각 미지정.
  startAt: '', endAt: '',
};

// QA-127: RadioGroup 은 문자열 값만 다루므로 boolean<->string 어댑터를 둔다.
const WINNER_ANNOUNCE_TRUE = 'true';
const WINNER_ANNOUNCE_FALSE = 'false';

/**
 * Derive a latin slug from the title for the BE-required `eventKey`.
 * Non-latin titles (Korean) collapse to empty → caller falls back to a short uid.
 */
const slugify = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

const shortUid = (): string => crypto.randomUUID().replace(/-/g, '').slice(0, 12);

/** Auto-generate a human-unreadable but valid eventKey (operator never sees it). */
const generateEventKey = (title: string): string => {
  const base = slugify(title);
  return base ? `${base}-${shortUid()}`.slice(0, 64) : `event-${shortUid()}`;
};

export const EventEditPage = () => {
  const { t } = useTranslation('common');
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  // C-5: '/cms/events/new' 는 정적 라우트라 :eventId 파라미터가 없다.
  // 파라미터가 없거나 'new' 이면 등록 모드 → 빈 eventId 로 하위 리소스(targets) 호출 방지.
  const isNew = !eventId || eventId === 'new';
  const id = !isNew ? eventId : '';

  const query = useEventQuery(id);
  const { create, update, remove } = useEventMutation();

  const [tab, setTab] = useState<'basic' | 'targets' | 'collection' | 'consent'>('basic');
  const [form, setForm] = useState<EventRequest>(EMPTY);
  // QA-122 — locale 별 제목/본문(en/ja/zh). ko-KR 은 form.title/description 정본. 빈 값 = SF ko 폴백.
  const [titleTranslations, setTitleTranslations] = useState<Partial<Record<ContentLocale, string>>>({});
  const [descTranslations, setDescTranslations] = useState<Partial<Record<ContentLocale, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const uploads = useImageUploadQueue();
  const uploadImage = useEventImageUpload();

  useEffect(() => {
    if (query.data) {
      // 추첨정책(예상 당첨자 수/추첨 방식/발표 채널/비당첨 안내)은 C-9로 폼에서 제거.
      // 기존 BE 값은 변형 없이 보존하여 그대로 재전송한다.
      setForm({
        eventId: query.data.eventId,
        eventKey: query.data.eventKey,
        title: query.data.title,
        description: query.data.description ?? '',
        thumbnailUrl: query.data.thumbnailUrl,
        startDate: query.data.startDate,
        endDate: query.data.endDate,
        status: query.data.status,
        isActive: query.data.isActive,
        sortOrder: query.data.sortOrder,
        winnerCount: query.data.winnerCount,
        drawingMethod: query.data.drawingMethod,
        contactChannel: query.data.contactChannel,
        consolationMessage: query.data.consolationMessage ?? '',
        // QA-127: BE additive — 미존재(undefined) 시 발표 이벤트(true) 폴백(기존 행 무회귀).
        winnerAnnounceEnabled: query.data.winnerAnnounceEnabled ?? true,
        // ID46: BE additive 라 미존재(undefined) 시 false 폴백.
        displayReservationEnabled: query.data.displayReservationEnabled ?? false,
        // 15-D-3(ID86): Instant(ISO) → datetime-local 표시값. 미지정(null) 시 ''.
        startAt: instantToLocalInput(query.data.startAt),
        endAt: instantToLocalInput(query.data.endAt),
      });
      // QA-122: locale 별 제목/본문 prefill(en/ja/zh). 정본 ko 는 title/description 본필드.
      setTitleTranslations(pickLocaleHtml(query.data.translations, EVENT_TITLE_FIELD));
      setDescTranslations(pickLocaleHtml(query.data.translations, EVENT_DESC_FIELD));
    }
  }, [query.data]);

  const set = <K extends keyof EventRequest>(key: K, val: EventRequest[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setError(null);
    // 15-D-3(ID86): 시작/종료 "시각"(datetime-local) → Instant 변환. 둘 다 지정 시 종료<시작 1차 검증(UX,
    // BE 도 거부). 미입력 한쪽/둘 다 = null(시각 미지정 = 기존 동작, 날짜만).
    const startAt = localInputToInstant(form.startAt);
    const endAt = localInputToInstant(form.endAt);
    if (isEndBeforeStart(startAt, endAt)) {
      setError(
        t('cms.events.fields.endAtBeforeStart', {
          defaultValue: '종료 시각은 시작 시각보다 빠를 수 없습니다.',
        }),
      );
      return;
    }
    try {
      const resolved = await uploads.flush();
      // 썸네일 필드가 비활성 탭에서 언마운트되면 resolved 에 키가 없다 → 기존 값 보존.
      const thumbnailUrl =
        'thumbnailUrl' in resolved && typeof resolved.thumbnailUrl === 'string'
          ? resolved.thumbnailUrl
          : form.thumbnailUrl;
      // QA-122: locale 별 제목/본문을 translations 맵으로(빈 로케일 제외=ko 폴백). 모두 비면 생략.
      const translations = buildLocaleMultiFieldTranslations<EventTranslation>({
        [EVENT_TITLE_FIELD]: titleTranslations,
        [EVENT_DESC_FIELD]: descTranslations,
      });
      const merged: EventRequest = {
        ...form,
        // QA-365 — 진행 상태는 시작일·종료일(KST)로 정해진다. 폼에서 고르지 않고 저장 시 도출(BE 필수 값).
        status: deriveEventStatus(form.startDate, form.endDate, todayKst()),
        thumbnailUrl,
        startAt,
        endAt,
        ...(translations ? { translations } : {}),
      };
      // [event-form-fix] BE 필수(EventCampaignRequest @NotBlank/@NotNull: title/thumbnailUrl/
      // startDate/endDate)를 제출 전 검증 — 종전엔 빈 값 그대로 보내 400("create failed: 400")로
      // 원인 표시 없이 실패했고, /new 에 머물러 탭이 계속 비활성 = "수정도 고장" 오인의 근원.
      const missing: string[] = [];
      if (!merged.title.trim()) missing.push(t('cms.events.fields.title'));
      if (!merged.thumbnailUrl) missing.push(t('cms.events.fields.thumbnailUrl'));
      if (!merged.startDate) missing.push(t('cms.events.fields.startDate'));
      if (!merged.endDate) missing.push(t('cms.events.fields.endDate'));
      if (missing.length > 0) {
        setError(
          t('cms.events.validation.required', {
            fields: missing.join(' · '),
            defaultValue: `필수 입력이 비어 있습니다: ${missing.join(' · ')}`,
          }),
        );
        return;
      }
      if (isNew) {
        // create.mutationFn 은 void 반환(서버 echo 없음). eventId 는 여기서 클라이언트가
        // 확정하므로(아래 newId), 등록 직후 그 id 의 수정 페이지로 이동해 같은 컴포넌트가
        // 수정(UPDATE) 모드로 전환된다 = "등록하고 → 이어서 설정" 흐름.
        const newId = crypto.randomUUID();
        // QA-365 — 정렬 순서 수기 입력을 없앴다. 공개 목록이 sortOrder 큰 순(등록 역순)이라 최댓값 + 1.
        // 목록 조회가 실패하면 종전 값(0)으로 등록한다.
        const sortOrder = await fetchAllEvents().then(nextEventSortOrder, () => merged.sortOrder);
        await create.mutateAsync({
          ...merged,
          sortOrder,
          eventId: newId,
          eventKey: generateEventKey(form.title),
        });
        // 등록 후 list 가 아니라 수정 페이지로 → 대상 상품·수집 항목을 이어서 설정.
        toast.success('이벤트가 등록되었습니다. 이어서 대상 상품·수집 항목을 설정하세요.');
        navigate(`/cms/events/${newId}`);
        return;
      }
      await update.mutateAsync({ eventId: id, body: merged });
      navigate('/cms/events');
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장 실패');
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(id);
      navigate('/cms/events');
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제 실패');
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending;
  // 수정 모드에서 기존 데이터 로드 전에는 저장을 막는다(하이드레이션 레이스). 막지 않으면
  // 폼이 EMPTY 기본값일 때 저장돼 stale 값이 영속된다. isLoading(v5: enabled+fetching)은
  // new 모드(enabled=false/idle)에선 false 라 안전하다.
  const isLoadingExisting = !isNew && query.isLoading;

  const breadcrumb = [
    { label: 'CMS', href: '/cms/events' },
    { label: t('cms.events.title'), href: '/cms/events' },
    { label: isNew ? t('cms.create') : t('cms.edit') },
  ];


  // QA-127: 당첨자 발표 유무 선택지(라벨은 한국어 i18n — 내부 boolean 값 노출 금지).
  const winnerAnnounceOptions = [
    { value: WINNER_ANNOUNCE_TRUE, label: t('cms.events.fields.winnerAnnounceOptions.announce', { defaultValue: '발표 이벤트' }) },
    { value: WINNER_ANNOUNCE_FALSE, label: t('cms.events.fields.winnerAnnounceOptions.noAnnounce', { defaultValue: '미발표 이벤트' }) },
  ];

  const basicSections = (
    <>
      <SectionCard title={t('cms.events.sectionBasic')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('cms.events.fields.title')} density="compact" required>
            <Input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              maxLength={300}
              required
            />
          </FormSection>
          <FormSection label={t('cms.events.fields.thumbnailUrl')} density="compact" required>
            <ImageUploadField
              queue={uploads}
              name="thumbnailUrl"
              category="CMS_EVENT"
              value={form.thumbnailUrl || null}
              onChange={(url) => set('thumbnailUrl', url ?? '')}
              disabled={isBusy}
            />
          </FormSection>
          <FormSection label={t('cms.events.fields.startDate')} density="compact" required>
            <Input
              type="date"
              value={form.startDate}
              onChange={(e) => set('startDate', e.target.value)}
              className="max-w-[200px]"
            />
          </FormSection>
          <FormSection label={t('cms.events.fields.endDate')} density="compact" required>
            <Input
              type="date"
              value={form.endDate}
              onChange={(e) => set('endDate', e.target.value)}
              className="max-w-[200px]"
            />
          </FormSection>
          {/* 15-D-3(ID86): 이벤트 시작/종료 "시각"(시:분). 위 시작일/종료일(날짜)과 별개 선택 입력.
           * 비우면 시각 미지정(기존 동작, 날짜만 노출). 시각 지정 시 SF 가 'YYYY.MM.DD HH:mm'로 노출. */}
          <FormSection
            label={t('cms.events.fields.startAt', { defaultValue: '시작 시각 (선택)' })}
            density="compact"
            hint={t('cms.events.fields.startAtHint', {
              defaultValue: '비우면 시각 미지정(날짜만 노출). 시:분까지 지정하면 프론트에 시각이 함께 표시됩니다.',
            })}
          >
            <Input
              type="datetime-local"
              value={form.startAt ?? ''}
              onChange={(e) => set('startAt', e.target.value)}
              className="max-w-[240px]"
              aria-label={t('cms.events.fields.startAt', { defaultValue: '시작 시각 (선택)' })}
            />
          </FormSection>
          <FormSection
            label={t('cms.events.fields.endAt', { defaultValue: '종료 시각 (선택)' })}
            density="compact"
            hint={t('cms.events.fields.endAtHint', {
              defaultValue: '비우면 시각 미지정. 시작/종료 시각을 모두 지정하면 종료가 시작보다 빠를 수 없습니다.',
            })}
          >
            <Input
              type="datetime-local"
              value={form.endAt ?? ''}
              onChange={(e) => set('endAt', e.target.value)}
              className="max-w-[240px]"
              aria-label={t('cms.events.fields.endAt', { defaultValue: '종료 시각 (선택)' })}
            />
          </FormSection>
          {/* QA-365 — "상태"(예정/진행 중/종료) 선택을 없앴다. 시작일·종료일로 정해지며 저장 시 도출한다. */}
          {/* QA-127: 당첨자 발표 유무. 발표 이벤트(true)=SF 가 당첨자 행 노출, 미발표 이벤트(false)=행 숨김.
           * RadioGroup 은 문자열 값이라 boolean<->string 어댑터로 form.winnerAnnounceEnabled 와 동기. */}
          <FormSection
            label={t('cms.events.fields.winnerAnnounceEnabled', { defaultValue: '당첨자 발표 유무' })}
            density="compact"
            required
          >
            <RadioGroup
              value={form.winnerAnnounceEnabled ? WINNER_ANNOUNCE_TRUE : WINNER_ANNOUNCE_FALSE}
              onValueChange={(v) => set('winnerAnnounceEnabled', v === WINNER_ANNOUNCE_TRUE)}
              options={winnerAnnounceOptions}
              orientation="horizontal"
              aria-label={t('cms.events.fields.winnerAnnounceEnabled', { defaultValue: '당첨자 발표 유무' })}
            />
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title={t('cms.events.sectionContent')}>
        {/* QA-122 — 제목·본문을 로케일(ko/en/ja/zh)별로 입력. ko 정본(상단 제목과 동기),
            타 로케일 빈 값 = SF ko 폴백. */}
        <LocaleHtmlTabs
          baseValue={form.description}
          onBaseChange={(html) => set('description', html)}
          translations={descTranslations}
          onTranslationChange={(locale, html) =>
            setDescTranslations((prev) => ({ ...prev, [locale]: html }))
          }
          titleField={{
            label: t('cms.events.fields.title'),
            baseValue: form.title,
            onBaseChange: (v) => set('title', v),
            translations: titleTranslations,
            onTranslationChange: (locale, v) =>
              setTitleTranslations((prev) => ({ ...prev, [locale]: v })),
            placeholder: t('cms.events.fields.titlePlaceholder', { defaultValue: '해당 언어 이벤트 제목' }),
            maxLength: 300,
          }}
          onUploadImage={uploadImage}
          imageGuide={editorImageGuideFor('EVENT_BODY')}
          onUploadError={(e) => setError(e instanceof Error ? e.message : '이미지 업로드에 실패했습니다.')}
          ariaLabelPrefix={t('cms.events.fields.description')}
        />
      </SectionCard>

      <SectionCard title={t('cms.events.sectionDisplay')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('cms.events.fields.isActive')} density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label={t('cms.events.fields.isActive')}
            />
          </FormSection>
          {/* ID46: 노출 예약. 켜면 BE 가 시작일~종료일(KST, 경계 포함) 윈도우로 공개 노출을 게이트한다.
           * 활성 여부(isActive)와 병존 — 둘 다 통과해야 공개. */}
          <FormSection
            label={t('cms.events.fields.displayReservationEnabled', { defaultValue: '노출 예약' })}
            density="compact"
            hint={t('cms.events.fields.displayReservationHint', {
              defaultValue: '켜면 위 시작일~종료일 기간에만 공개에 노출됩니다(KST, 경계 포함). 기간 밖이면 자동으로 숨겨집니다.',
            })}
          >
            <Switch
              checked={form.displayReservationEnabled}
              onCheckedChange={(v) => set('displayReservationEnabled', v)}
              aria-label={t('cms.events.fields.displayReservationEnabled', { defaultValue: '노출 예약' })}
            />
          </FormSection>
          {/* QA-365 — 정렬 순서 수기 입력 제거. 새 이벤트는 등록 시 자동(최댓값 + 1), 기존 값은 그대로 보존. */}
        </div>
      </SectionCard>
    </>
  );

  return (
    <PageShell
      title={isNew ? t('cms.events.createTitle') : t('cms.events.editTitle')}
      breadcrumb={breadcrumb}
      data-testid="event-edit-page"
      actions={
        <div className="flex gap-2">
          {/* ID58: 저장된 이벤트에 한해 미리보기 URL 생성(미저장 신규는 contentId 없음 → 비활성). */}
          {!isNew && <PreviewUrlButton contentType="EVENT" contentId={id} />}
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              {t('cms.delete')}
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || uploads.isUploading || isLoadingExisting}>
            {uploads.isUploading
              ? '이미지 업로드 중…'
              : isLoadingExisting
                ? t('cms.loading', { defaultValue: '불러오는 중…' })
                : t('cms.save')}
          </Button>
        </div>
      }
    >
      {(error || uploads.error) && (
        <div
          role="alert"
          className="rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {error ?? uploads.error}
        </div>
      )}

      {/* 등록·수정 외관 정합: 등록 모드에도 수정과 동일한 3탭을 노출한다. 미영속(등록) 상태에서는
       * 하위폼(EventTargetForm / EventCollectionFieldsForm)이 빈 eventId 가드로 "먼저 이벤트를
       * 저장한 뒤 설정" 안내를 띄운다 → 기본 정보 저장(create) 후 수정 페이지로 이동하면 그때
       * 같은 폼에서 대상 상품·수집 항목을 이어서 설정할 수 있다(handleSubmit). */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'basic' | 'targets' | 'collection' | 'consent')}>
        <TabsList>
          <TabsTrigger value="basic">{t('cms.events.tabs.basic')}</TabsTrigger>
          <TabsTrigger value="targets">{t('cms.events.tabs.targets')}</TabsTrigger>
          {/* QA-447 — 응모 추가 입력은 상품 등록의 추가입력으로 받는다(중복) → 탭 비활성화. 컴포넌트는 남겨 둔다. */}
          <TabsTrigger
            value="collection"
            data-testid="event-tab-collection"
            disabled
            title="응모 추가 입력은 상품 등록의 추가입력에서 설정합니다."
          >
            응모/수집 항목 설정
          </TabsTrigger>
          {/* R4-A ⑤ — 이벤트 응모 동의 약관(상세 본문) 편집 탭. */}
          <TabsTrigger value="consent" data-testid="event-tab-consent">
            응모 동의 약관
          </TabsTrigger>
        </TabsList>
        <TabsContent value="basic" className="flex flex-col gap-4 mt-4">
          {basicSections}
        </TabsContent>
        <TabsContent value="targets" className="mt-4">
          <SectionCard title={t('cms.events.targets.sectionTitle')}>
            <EventTargetForm eventId={id} />
          </SectionCard>
        </TabsContent>
        <TabsContent value="collection" className="mt-4">
          <EventCollectionFieldsForm eventId={id} />
        </TabsContent>
        <TabsContent value="consent" className="mt-4">
          <EventConsentTermsForm eventId={id} />
        </TabsContent>
      </Tabs>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.events.title')} 삭제</DialogTitle>
            <DialogDescription>
              {t('cms.events.confirmDelete', { title: form.title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
